import {useEffect} from 'react'
import {hasAnalyticsConsent} from '../ketchConsentManager'

const area = (rect) => (rect ? rect.width * rect.height : 0)

const describeNode = (node) => {
    const parts = []
    for (let el = node; el && el.nodeType === 1 && parts.length < 4; el = el.parentElement) {
        if (el.id) {
            parts.unshift(`${el.tagName.toLowerCase()}#${el.id}`)
            break
        }
        const classes = Array.from(el.classList)
            .filter((name) => !name.startsWith('css-'))
            .slice(0, 2)
        parts.unshift([el.tagName.toLowerCase(), ...classes].join('.'))
    }
    return parts.join(' > ') || undefined
}

const useLayoutShiftReporting = () => {
    useEffect(() => {
        if (!window.PerformanceObserver?.supportedEntryTypes?.includes('layout-shift')) return

        const metricId = `cls-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        let windowValue = 0
        let windowEntries = []
        let worst = {value: 0, entries: [], path: window.location.pathname}
        let reported = 0

        const observer = new PerformanceObserver((list) => {
            list.getEntries().forEach((entry) => {
                if (entry.hadRecentInput) return
                const first = windowEntries[0]
                const last = windowEntries[windowEntries.length - 1]
                if (
                    last &&
                    entry.startTime - last.startTime < 1000 &&
                    entry.startTime - first.startTime < 5000
                ) {
                    windowValue += entry.value
                    windowEntries.push(entry)
                } else {
                    windowValue = entry.value
                    windowEntries = [entry]
                }
                if (windowValue > worst.value) {
                    worst = {
                        value: windowValue,
                        entries: windowEntries,
                        path: window.location.pathname
                    }
                }
            })
        })
        observer.observe({type: 'layout-shift', buffered: true})

        const report = (event) => {
            if (event.type === 'visibilitychange' && document.visibilityState !== 'hidden') return
            if (worst.value <= reported || !hasAnalyticsConsent()) return
            reported = worst.value
            const largest = worst.entries.reduce((a, b) => (b.value > a.value ? b : a))
            const size = (source) => Math.max(area(source.previousRect), area(source.currentRect))
            const source = (largest.sources || [])
                .filter((candidate) => candidate.node)
                .sort((a, b) => size(b) - size(a))[0]
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                event: 'web_vitals',
                metric_name: 'CLS',
                metric_id: metricId,
                metric_value: Number(worst.value.toFixed(4)),
                metric_rating:
                    worst.value <= 0.1
                        ? 'good'
                        : worst.value <= 0.25
                        ? 'needs-improvement'
                        : 'poor',
                metric_element: source ? describeNode(source.node) : undefined,
                page_path: worst.path
            })
        }

        document.addEventListener('visibilitychange', report)
        window.addEventListener('pagehide', report)
        return () => {
            observer.disconnect()
            document.removeEventListener('visibilitychange', report)
            window.removeEventListener('pagehide', report)
        }
    }, [])
}

export default useLayoutShiftReporting
